/**
 * Desk model (docs/desk-ux.md) — the pure parts of the home overview, unit-tested in
 * desk.test.ts:
 *
 *   - the performance summary card: this month's net P&L + ROI and a per-day series
 *   - Needs attention: venue problems, working orders, upgrade nudges (max 2, tried first,
 *     dismissible per browser)
 *   - Getting started: three steps, each ticked from data the app already has
 *   - the Live & soon strip: live games first, then games starting within ~3 hours
 *
 * Plus the small per-browser lists the Desk keeps (features tried, nudges dismissed, Getting
 * started finished), each read and written inside try/catch: storage may be unavailable.
 */
import { bucketOf, isGame, startMs, type ContestLike, type LiveSignal, type PriceStatus } from "./marketsBoard.js";

/* ============================================================ performance */

export interface PerfGroupLike {
  key?: string | null;
  net_pl?: number | null;
}
export interface PerfCurrencyLike {
  funding?: { currency?: string | null; is_real_money?: boolean | null } | null;
  net_pl?: number | null;
  roi?: number | null;
  settled_count?: number | null;
  groups?: PerfGroupLike[] | null;
}
export interface PerfLike {
  by_currency?: PerfCurrencyLike[] | null;
}

/** The real-money (USD) or play-money (ATLAS) bucket of a performance payload. */
export function pickPerfCurrency<C extends PerfCurrencyLike>(
  perf: { by_currency?: C[] | null } | undefined,
  real: boolean,
): C | undefined {
  return perf?.by_currency?.find((c) => (c.funding?.is_real_money ?? false) === real) ?? undefined;
}

const pad2 = (n: number) => String(n).padStart(2, "0");
/** Local YYYY-MM-DD. */
export const dayKey = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** [from, to) ISO YYYY-MM-DD window covering the calendar month containing `d`. */
export function monthWindow(d: Date): { from: string; to: string } {
  const from = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return { from, to: `${next.getFullYear()}-${pad2(next.getMonth() + 1)}-01` };
}

export interface PerfSummary {
  currency: "USD" | "ATLAS";
  net: number;
  roi: number | null;
  settled: number;
  /** One entry per day from the 1st to today (or month end), in order; days with nothing settled are 0. */
  days: { key: string; net: number }[];
  /** False when the day breakdown wasn't available (no sparkline). */
  hasDays: boolean;
}

/**
 * The summary card's numbers. `total` is `group_by=none`, `byDay` is `group_by=day` for the same
 * month window. Day groups come back in no particular order (and only for days with settlements),
 * so the series is rebuilt by date with gaps as 0.
 */
export function perfSummary(
  total: PerfLike | undefined,
  byDay: PerfLike | undefined,
  real: boolean,
  now: Date,
): PerfSummary {
  const cur = pickPerfCurrency(total, real);
  const dayCur = pickPerfCurrency(byDay, real);
  const groups = dayCur?.groups ?? null;
  const byKey = new Map<string, number>();
  for (const g of groups ?? []) if (g.key) byKey.set(g.key, (byKey.get(g.key) ?? 0) + (g.net_pl ?? 0));
  const days: { key: string; net: number }[] = [];
  const last = now.getDate();
  for (let d = 1; d <= last; d++) {
    const key = dayKey(new Date(now.getFullYear(), now.getMonth(), d));
    days.push({ key, net: byKey.get(key) ?? 0 });
  }
  const net = cur?.net_pl ?? 0;
  const settled = cur?.settled_count ?? 0;
  return {
    currency: real ? "USD" : "ATLAS",
    net,
    roi: settled > 0 && cur?.roi != null && Number.isFinite(cur.roi) ? cur.roi : null,
    settled,
    days,
    hasDays: byDay != null && groups != null,
  };
}

/** "+$38.20" / "−$4.10" / "+26 ATLAS". */
export function signedAmount(n: number, ccy: "USD" | "ATLAS"): string {
  const sign = n > 0.005 ? "+" : n < -0.005 ? "−" : "";
  const abs = Math.abs(n);
  if (ccy === "ATLAS") return `${sign}${Math.round(abs).toLocaleString()} ATLAS`;
  return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "4.1%" / "−2.0%" (no plus: the card reads "ROI 4.1%"). */
export function roiText(roi: number | null): string {
  if (roi == null || !Number.isFinite(roi)) return "—";
  const pct = roi * 100;
  return `${pct < -0.05 ? "−" : ""}${Math.abs(pct).toFixed(1)}%`;
}

/* ========================================================= needs attention */

/** The subset of a `/auth/account/balances` partner the Desk reads. */
export interface VenueLike {
  partner_id?: string | null;
  partner_name?: string | null;
  status?: string | null;
  credential_status?: string | null;
  is_stale?: boolean | null;
  balance_source?: string | null;
  balance_as_of?: string | null;
}

/** Features greenbook can do that a session may not have been granted, in default priority order. */
export const NUDGES: { scope: string; title: string; body: string }[] = [
  { scope: "trade:execute", title: "Real-money trading", body: "Place real orders on the venues you’ve connected." },
  { scope: "stream:realtime", title: "Live streaming", body: "Prices and fills update the moment they move." },
  { scope: "depth:read", title: "Order-book depth", body: "See each venue’s full ladder, not just the best price." },
  { scope: "pricing:history", title: "Price history", body: "Charts of how a price has moved." },
  { scope: "gamestate:read", title: "Live scores", body: "Clocks and scores on games in progress." },
];
export const MAX_NUDGES = 2;

export type AttentionItem =
  | { kind: "venue"; id: string; venue: string; problem: "reconnect" | "stale"; asOf: string | null }
  | { kind: "working"; id: "working"; count: number; waiting: number }
  | { kind: "nudge"; id: string; scope: string; title: string; body: string; tried: boolean };

const isConnected = (v: VenueLike) => (v.credential_status ?? "").toLowerCase() === "connected";

/**
 * A real venue's problem, if any. `reconnect`: an active venue whose credential isn't
 * `connected` (the values aren't documented; live we've seen `connected` and `pending`). A venue
 * the account has switched off (`status: inactive`) isn't flagged. `stale`: connected, but the
 * balance snapshot is behind (`is_stale`); the practice wallet is exact and never stale.
 */
export function venueProblem(v: VenueLike): "reconnect" | "stale" | null {
  if (v.balance_source === "om_wallet") return null;
  if ((v.status ?? "active").toLowerCase() !== "active") return null;
  if (!isConnected(v)) return "reconnect";
  return v.is_stale ? "stale" : null;
}

/** A connected real venue (for Getting started and the Real card). */
export function isConnectedVenue(v: VenueLike): boolean {
  return (v.status ?? "active").toLowerCase() === "active" && isConnected(v);
}

/**
 * Needs attention, in display order: venue problems, then working orders, then at most
 * `MAX_NUDGES` upgrade nudges. A nudge is a catalog feature this session wasn't granted; ones the
 * user has tried (opened the unlock explainer for, most recent first) come before the rest, and
 * dismissed ones never show. `granted: null` means the scopes aren't knowable (a pasted key), so
 * there are no nudges. `suppress` drops scopes another card already offers (Getting started's
 * "Unlock real money"); `triedOnly` keeps just the tried ones (a new user working through
 * Getting started isn't shown upsells for things they haven't reached for).
 */
export function attentionItems(input: {
  venues: VenueLike[];
  working: { count: number; waiting: number };
  granted: readonly string[] | null;
  tried: readonly string[];
  dismissed: readonly string[];
  suppress?: readonly string[];
  triedOnly?: boolean;
}): AttentionItem[] {
  const out: AttentionItem[] = [];
  for (const v of input.venues) {
    const problem = venueProblem(v);
    if (!problem) continue;
    out.push({
      kind: "venue",
      id: `venue:${v.partner_id ?? v.partner_name ?? "?"}`,
      venue: v.partner_name ?? v.partner_id ?? "A venue",
      problem,
      asOf: v.balance_as_of ?? null,
    });
  }
  if (input.working.count > 0) out.push({ kind: "working", id: "working", count: input.working.count, waiting: input.working.waiting });
  if (input.granted) {
    const granted = new Set(input.granted);
    const skip = new Set([...input.dismissed, ...(input.suppress ?? [])]);
    const triedRank = new Map(input.tried.map((s, i) => [s, i]));
    const candidates = NUDGES.map((n, i) => ({ ...n, i })).filter((n) => !granted.has(n.scope) && !skip.has(n.scope) && (!input.triedOnly || triedRank.has(n.scope)));
    candidates.sort((a, b) => {
      const ta = triedRank.get(a.scope);
      const tb = triedRank.get(b.scope);
      if (ta != null && tb != null) return ta - tb;
      if (ta != null) return -1;
      if (tb != null) return 1;
      return a.i - b.i;
    });
    for (const n of candidates.slice(0, MAX_NUDGES)) {
      out.push({ kind: "nudge", id: `nudge:${n.scope}`, scope: n.scope, title: n.title, body: n.body, tried: triedRank.has(n.scope) });
    }
  }
  return out;
}

/* ========================================================= getting started */

export type StepId = "paper" | "venue" | "real";
export interface GettingStarted {
  steps: { id: StepId; label: string; hint: string; done: boolean }[];
  doneCount: number;
  complete: boolean;
}

export function gettingStarted(input: { hasPaperOrder: boolean; hasVenue: boolean; canTrade: boolean }): GettingStarted {
  const steps: GettingStarted["steps"] = [
    { id: "paper", label: "Place a paper order", hint: "Play money, every venue", done: input.hasPaperOrder },
    { id: "venue", label: "Connect a venue", hint: "On OpenMarkets — your own sportsbook or exchange account", done: input.hasVenue },
    { id: "real", label: "Unlock real money", hint: "Trade on your connected venues", done: input.canTrade },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  return { steps, doneCount, complete: doneCount === steps.length };
}

/**
 * Whether the Getting started card shows: never until everything it depends on has loaded (no
 * flash), never once it's been completed in this browser, and only while a step is left.
 */
export function showGettingStarted(gs: GettingStarted | null, finishedHere: boolean): boolean {
  return !!gs && !finishedHere && !gs.complete;
}

/* ================================================================== in play */

/**
 * The games In play lists: the ones held that are live (in Portfolio's order), then those still
 * to start, soonest first, up to `n`. Games that already started and aren't live (finished, or an
 * old order the venue never closed) and futures aren't "soonest or live", so they stay on
 * Portfolio.
 */
export function inPlayTop<G extends { contestId: string }>(
  sorted: G[],
  contestOf: (id: string) => ContestLike | undefined,
  isLive: (g: G) => boolean,
  now: number,
  n = 3,
): G[] {
  const live = sorted.filter(isLive);
  const upcoming = sorted
    .filter((g) => {
      if (isLive(g)) return false;
      const c = contestOf(g.contestId);
      return !!c && isGame(c) && startMs(c) > now;
    })
    .sort((a, b) => startMs(contestOf(a.contestId)!) - startMs(contestOf(b.contestId)!));
  return [...live, ...upcoming].slice(0, n);
}

/* ============================================================ live & soon */

export const LIVE_SOON_CAP = 10;

/**
 * The Live & soon strip: games (not futures) that are live — most recently started first, as on
 * Markets — then those starting within Markets' "Starting soon" window (~3 h), soonest first.
 * Cards known to have nothing to trade drop out (the Markets "tradeable only" default), then the
 * list is capped.
 */
export function liveAndSoon<C extends ContestLike>(
  contests: C[],
  liveOf: (c: C) => LiveSignal,
  statusOf: (c: C) => PriceStatus,
  now: number,
  cap = LIVE_SOON_CAP,
): { contest: C; bucket: "live" | "soon" }[] {
  const live: C[] = [];
  const soon: C[] = [];
  for (const c of contests) {
    if (!isGame(c) || c.has_markets === false || statusOf(c) === "empty") continue;
    const b = bucketOf(c, liveOf(c), now);
    if (b === "live") live.push(c);
    else if (b === "soon") soon.push(c);
  }
  const asc = (a: C, b: C) => (startMs(a) || Infinity) - (startMs(b) || Infinity) || a.id.localeCompare(b.id);
  live.sort((a, b) => -asc(a, b));
  soon.sort(asc);
  return [
    ...live.map((contest) => ({ contest, bucket: "live" as const })),
    ...soon.map((contest) => ({ contest, bucket: "soon" as const })),
  ].slice(0, cap);
}

/* ================================================== per-browser storage */

const TRIED_KEY = "gb.unlock-tried";
const DISMISSED_KEY = "gb.desk.dismissed-nudges";
const GS_DONE_KEY = "gb.desk.getting-started-done";

function readList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const v = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function writeList(key: string, list: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* storage unavailable — the list just won't persist */
  }
}

/** Put `scope` at the front of a most-recent-first list (no duplicates, capped). */
export function bumpRecent(list: readonly string[], scope: string, cap = 20): string[] {
  return [scope, ...list.filter((s) => s !== scope)].slice(0, cap);
}

/** Features (scopes) whose unlock explainer was opened in this browser, most recent first. */
export function readTried(): string[] {
  return readList(TRIED_KEY);
}
/** Called by `showUnlock`: remember that the user tried this feature. */
export function recordTried(scope: string): void {
  writeList(TRIED_KEY, bumpRecent(readList(TRIED_KEY), scope));
}
export function readDismissed(): string[] {
  return readList(DISMISSED_KEY);
}
export function dismissNudge(scope: string): string[] {
  const next = bumpRecent(readList(DISMISSED_KEY), scope, 50);
  writeList(DISMISSED_KEY, next);
  return next;
}
export function readGettingStartedDone(): boolean {
  try {
    return localStorage.getItem(GS_DONE_KEY) === "1";
  } catch {
    return false;
  }
}
export function markGettingStartedDone(): void {
  try {
    localStorage.setItem(GS_DONE_KEY, "1");
  } catch {
    /* ignore */
  }
}
